import { Dispatch, SetStateAction, useState } from 'react';
import { Folder } from '../types';
import { createSecretCollectionPin, SecretCollectionPin, verifySecretCollectionPin } from '../utils/secretCollections';
import { loadFromStorage, removeFromStorage, saveToStorage, STORAGE_KEYS } from '../utils/storageUtils';

type PinModalMode = 'setup' | 'unlock';
type PinPurpose = 'setPin' | 'changePin' | 'remove' | 'createSecret' | 'makeSecret' | 'makeRegular' | 'unlock';

type PendingAction =
    | { type: 'setPin' }
    | { type: 'changePin' }
    | { type: 'removePin' }
    | { type: 'unlock' }
    | { type: 'createSecretCollection'; onCreate: () => void }
    | { type: 'updateCollectionStatus'; collectionId: string; secret: boolean };

interface PinModalState {
    mode: PinModalMode;
    purpose: PinPurpose;
}

interface UseSecretCollectionsOptions {
    setFolders: Dispatch<SetStateAction<Folder[]>>;
}

export const useSecretCollections = ({ setFolders }: UseSecretCollectionsOptions) => {
    const [areSecretCollectionsVisible, setAreSecretCollectionsVisible] = useState(false);
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
    const [pinModal, setPinModal] = useState<PinModalState | null>(null);
    const [showDeletePinConfirmation, setShowDeletePinConfirmation] = useState(false);

    const hasPin = () => Boolean(loadFromStorage<SecretCollectionPin>(STORAGE_KEYS.SECRET_COLLECTION_PIN));

    const openPinModal = (mode: PinModalMode, purpose: PinPurpose, action: PendingAction) => {
        setPendingAction(action);
        setPinModal({ mode, purpose });
    };

    const updateCollectionSecretStatus = (collectionId: string, secret: boolean) => {
        setFolders((currentFolders) => currentFolders.map((folder) => ({
            ...folder,
            collections: (folder.collections || []).map((collection) =>
                collection.id === collectionId ? { ...collection, secret } : collection,
            ),
        })));
    };

    const completePendingAction = (action: PendingAction) => {
        if (action.type === 'createSecretCollection') action.onCreate();
        if (action.type === 'updateCollectionStatus') {
            updateCollectionSecretStatus(action.collectionId, action.secret);
        }
        if (
            action.type === 'unlock'
            || action.type === 'createSecretCollection'
            || action.type === 'updateCollectionStatus'
        ) {
            setAreSecretCollectionsVisible(true);
        }
    };

    const requestSecretCollectionsVisibility = () => {
        if (areSecretCollectionsVisible) {
            setAreSecretCollectionsVisible(false);
            return;
        }

        openPinModal(hasPin() ? 'unlock' : 'setup', hasPin() ? 'unlock' : 'setPin', { type: 'unlock' });
    };

    const requestSetPin = () => openPinModal('setup', 'setPin', { type: 'setPin' });

    const requestChangePin = () => openPinModal('unlock', 'changePin', { type: 'changePin' });

    const requestDeletePin = () => openPinModal('unlock', 'remove', { type: 'removePin' });

    const requestSecretCollectionCreation = (onCreate: () => void) => {
        openPinModal(
            hasPin() ? 'unlock' : 'setup',
            'createSecret',
            { type: 'createSecretCollection', onCreate },
        );
    };

    const requestCollectionSecretStatusChange = (collectionId: string, secret: boolean) => {
        openPinModal(
            hasPin() ? 'unlock' : 'setup',
            secret ? 'makeSecret' : 'makeRegular',
            { type: 'updateCollectionStatus', collectionId, secret },
        );
    };

    const handlePinSubmit = async (pin: string): Promise<boolean> => {
        if (!pendingAction || !pinModal) return false;

        try {
            if (pinModal.mode === 'setup') {
                saveToStorage(STORAGE_KEYS.SECRET_COLLECTION_PIN, await createSecretCollectionPin(pin));
                completePendingAction(pendingAction);
                setPendingAction(null);
                setPinModal(null);
                return true;
            }

            const secretPin = loadFromStorage<SecretCollectionPin>(STORAGE_KEYS.SECRET_COLLECTION_PIN);
            if (!secretPin || !(await verifySecretCollectionPin(pin, secretPin))) return false;

            if (pendingAction.type === 'changePin') {
                setPinModal({ mode: 'setup', purpose: 'changePin' });
                return true;
            }

            if (pendingAction.type === 'removePin') {
                setPendingAction(null);
                setPinModal(null);
                setShowDeletePinConfirmation(true);
                return true;
            }

            completePendingAction(pendingAction);
            setPendingAction(null);
            setPinModal(null);
            return true;
        } catch (error) {
            console.error('Unable to process the secret collections PIN.', error);
            return false;
        }
    };

    const cancelPinModal = () => {
        setPendingAction(null);
        setPinModal(null);
    };

    const confirmDeletePin = () => {
        removeFromStorage(STORAGE_KEYS.SECRET_COLLECTION_PIN);
        setFolders((currentFolders) => currentFolders.map((folder) => ({
            ...folder,
            collections: (folder.collections || []).map((collection) => ({ ...collection, secret: false })),
        })));
        setAreSecretCollectionsVisible(false);
        setShowDeletePinConfirmation(false);
    };

    return {
        areSecretCollectionsVisible,
        pinModal,
        showDeletePinConfirmation,
        requestSecretCollectionsVisibility,
        requestSetPin,
        requestChangePin,
        requestDeletePin,
        requestSecretCollectionCreation,
        requestCollectionSecretStatusChange,
        handlePinSubmit,
        cancelPinModal,
        confirmDeletePin,
        cancelDeletePinConfirmation: () => setShowDeletePinConfirmation(false),
    };
};
